import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthContext } from '../../auth/AuthContext';
import appConfig from '../../config';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { queryAthenaLookup, updateForm } from '../../service/form';
import type { FetchFormResponse } from '../../service/types';
import { resolveSelectedFormId } from '../../utils/form-id';
import { isPastLondon } from '../../utils/londonTime';
import {
  buildCdrImport,
  type CdrChoice,
  type CdrImportSection,
  hasCdrData,
  hasExistingCdrData,
  saveCdrImport,
} from '../../utils/psfa-cdr-import/import';
import type { CdrSection } from '../../utils/psfa-cdr-import/workbook';
import { type CdrConflict, readCdrWorkbooks } from '../../utils/psfa-cdr-import/batch';
import BulkTableModal from './bulk-table/BulkTableModal';
import ExcelConflictFields from './bulk-table/ExcelConflictFields';

const SectionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px 44px minmax(160px, 230px);
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid #b1b4b6;
  input { width: 24px; height: 24px; accent-color: #00703c; }
  label { cursor: pointer; }
  select { min-width: 0; width: 100%; }
  small { display: block; color: #505a5f; margin-top: 5px; }
  .govuk-hint { margin: 0; }
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr) 36px minmax(0, 1fr);
    label { grid-column: 1 / -1; }
  }
`;
const Preview = styled.div`
  overflow-x: auto;
  margin-bottom: 24px;
  td, th { min-width: 130px; max-width: 340px; white-space: pre-wrap; overflow-wrap: anywhere; }
`;
type Props = { files: File[]; onClose?: () => void; onImported?: () => void };

export default function PsfaCdrImport({ files, onClose, onImported }: Props) {
  const { formConfig, tenant } = useFormConfig();
  const { data, refetch } = useFormData();
  const { fetchJWT } = useAuthContext();
  const location = useLocation();
  const formId = resolveSelectedFormId(tenant, location.search);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sections, setSections] = useState<CdrSection[]>([]);
  const [conflicts, setConflicts] = useState<CdrConflict[]>([]);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [choices, setChoices] = useState<Record<string, CdrChoice>>({});
  const [snapshot, setSnapshot] = useState<FetchFormResponse>();
  const [plan, setPlan] = useState<CdrImportSection[]>();
  const [saved, setSaved] = useState<string[]>([]);
  const [startedSaving, setStartedSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const canEdit = (value?: FetchFormResponse) =>
    tenant === 'psfa' &&
    !!formId &&
    ['owner', 'editor', 'admin'].includes(String(value?.role || '').toLowerCase()) &&
    ['active', 'rejected'].includes(String(value?.formState || '').toLowerCase()) &&
    !isPastLondon(formConfig.formCloseAt || value?.formCloseAt || undefined);
  const close = () => {
    if (!busy) {
      setOpen(false);
      onClose?.();
    }
  };

  const load = async (selectedFiles: File[]) => {
    setOpen(true);
    setBusy(true);
    setError('');
    setMessage('');
    setPlan(undefined);
    setSaved([]);
    setStartedSaving(false);
    setSections([]);
    setConflicts([]);
    setSelections({});
    try {
      if (!canEdit(data)) throw new Error('This form is not available for editing.');
      const parsed = await readCdrWorkbooks(selectedFiles, formConfig);
      setSections(parsed.sections);
      setConflicts(parsed.conflicts);
      setChoices(
        Object.fromEntries(
          parsed.sections.map((section) => [section.id, { selected: true, mode: 'overwrite' }]),
        ),
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The workbook could not be read.');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(files);
  }, [files]);

  const activeConflicts = conflicts.filter(
    (conflict) =>
      choices[conflict.sectionId]?.selected && choices[conflict.sectionId]?.mode !== 'keep',
  );

  const preview = async () => {
    setBusy(true);
    setError('');
    try {
      if (activeConflicts.some((conflict) => !selections[conflict.key]))
        throw new Error('Choose a value for each conflicting field before continuing.');
      const fresh = await refetch();
      if (fresh.error) throw fresh.error;
      if (!fresh.data || !canEdit(fresh.data))
        throw new Error('This form is not available for editing.');
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in again before importing.');
      const needsOrganisations = sections.some(
        (section) =>
          section.id === 'department-select' &&
          choices[section.id]?.selected &&
          choices[section.id]?.mode !== 'keep' &&
          hasCdrData(section),
      );
      const organisations = needsOrganisations
        ? (
            await queryAthenaLookup(idToken, {
              lookupType: 'psfaDepartmentList',
              environment:
                window.location.hostname.includes('preprod') ||
                appConfig.apiUrl?.includes('preprod')
                  ? 'preprod'
                  : 'prod',
              maxRows: 1000,
            })
          ).rows
        : [];
      const resolved = sections.map((section) => ({
        ...section,
        values: {
          ...section.values,
          ...Object.fromEntries(
            activeConflicts
              .filter((conflict) => conflict.sectionId === section.id)
              .map((conflict) => [
                conflict.field,
                conflict.options[Number(selections[conflict.key])].value,
              ]),
          ),
        },
      }));
      const next = buildCdrImport(resolved, choices, fresh.data, organisations);
      if (!next.length) throw new Error('Select at least one section containing data.');
      setSnapshot(fresh.data);
      setPlan(next);
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'The import preview could not be prepared.',
      );
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!plan || !snapshot || !formId) return;
    setBusy(true);
    setError('');
    try {
      if (!startedSaving) {
        const fresh = await refetch();
        if (fresh.error) throw fresh.error;
        if (!canEdit(fresh.data)) throw new Error('This form is not available for editing.');
        const keys = new Set([
          ...Object.keys(snapshot.answers),
          ...Object.keys(fresh.data?.answers || {}),
        ]);
        if (
          [...keys].some(
            (key) =>
              JSON.stringify(snapshot.answers[key]) !== JSON.stringify(fresh.data?.answers[key]),
          )
        )
          throw new Error(
            'Form data changed since the preview. Go back and review the import again.',
          );
      }
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in again before importing.');
      setStartedSaving(true);
      await saveCdrImport(
        plan.filter((item) => !saved.includes(item.section.id)),
        (update) => updateForm(idToken, update, { form_id: formId }),
        (item) => setSaved((previous) => [...previous, item.section.id]),
      );
      const imported = plan.filter((item) => item.mode !== 'keep');
      const drafts = imported.filter((item) => item.errors.length > 0).length;
      setMessage(
        `Imported ${imported.length} section${imported.length === 1 ? '' : 's'}: ${imported.length - drafts} complete, ${drafts} draft.${drafts ? ' Review draft sections to finish them.' : ''}`,
      );
      await refetch();
      setOpen(false);
      onImported?.();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The import could not be saved.');
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  if (tenant !== 'psfa' || (!open && !canEdit(data))) return null;
  return (
    <>
      {message && (
        <p className="govuk-body" role="status">
          {message}
        </p>
      )}
      {open && (
        <BulkTableModal
          key={plan ? 'preview' : 'selection'}
          title={plan ? 'Confirm CDR import' : 'Select sections to import'}
          width="min(100%, 1100px)"
          zIndex={1100}
          onClose={close}
        >
          <p className="govuk-body">
            {plan
              ? 'Review the resulting data below. Nothing is saved until you confirm.'
              : 'Sections are selected by default. Untick any you do not want to import. Empty sections are skipped.'}
          </p>
          {!plan && (
            <>
              <p className="govuk-body">
                For tables, choose Overwrite or Append. For single-response sections, choose Replace
                or Keep existing values. Kept sections are not changed.
              </p>
              {sections.map((section) => (
                <SectionRow key={section.id}>
                  <label className="govuk-label" htmlFor={`cdr-${section.id}`}>
                    {section.title}
                    <small>{section.group}</small>
                  </label>
                  <span className="govuk-hint" id={`cdr-data-${section.id}`}>
                    {hasCdrData(section)
                      ? section.page.pageType === 'list'
                        ? `${section.rows.length} row${section.rows.length === 1 ? '' : 's'} found`
                        : 'Data found'
                      : 'No data - skipped'}
                  </span>
                  <input
                    id={`cdr-${section.id}`}
                    aria-describedby={`cdr-data-${section.id}`}
                    type="checkbox"
                    checked={choices[section.id]?.selected ?? true}
                    disabled={busy}
                    onChange={(event) =>
                      setChoices({
                        ...choices,
                        [section.id]: { ...choices[section.id], selected: event.target.checked },
                      })
                    }
                  />
                  {hasExistingCdrData(section, data?.answers || {}) ? (
                    <select
                      className="govuk-select"
                      aria-label={`Existing data: ${section.title}`}
                      disabled={busy || !choices[section.id]?.selected}
                      value={choices[section.id]?.mode || 'overwrite'}
                      onChange={(event) =>
                        setChoices({
                          ...choices,
                          [section.id]: {
                            ...choices[section.id],
                            mode: event.target.value as CdrChoice['mode'],
                          },
                        })
                      }
                    >
                      <option value="overwrite">
                        {section.page.pageType === 'list' ? 'Overwrite' : 'Replace'}
                      </option>
                      {section.page.pageType === 'list' ? (
                        <option value="append">Append</option>
                      ) : (
                        <option value="keep">Keep existing values</option>
                      )}
                    </select>
                  ) : (
                    <span className="govuk-hint">No existing data</span>
                  )}
                </SectionRow>
              ))}
              <ExcelConflictFields
                conflicts={activeConflicts}
                selections={selections}
                onChange={setSelections}
                disabled={busy}
              />
            </>
          )}
          {plan?.map((item) => (
            <section key={item.section.id}>
              <h3 className="govuk-heading-m">{item.section.title}</h3>
              <p className="govuk-body">
                <strong>
                  {item.mode === 'keep'
                    ? 'Keep existing values'
                    : saved.includes(item.section.id)
                      ? 'Saved'
                      : item.errors.length
                        ? 'Save as draft'
                        : 'Save as complete'}
                </strong>{' '}
                ·{' '}
                {item.mode === 'append'
                  ? 'Append'
                  : item.mode === 'keep'
                    ? 'No changes'
                    : 'Replace'}
              </p>
              {item.section.notes.map((note) => (
                <p className="govuk-body" key={note}>
                  {note}
                </p>
              ))}
              {item.errors.length > 0 && (
                <details className="govuk-details">
                  <summary className="govuk-details__summary">
                    <span className="govuk-details__summary-text">
                      {item.errors.length} field{item.errors.length === 1 ? ' needs' : 's need'}{' '}
                      attention
                    </span>
                  </summary>
                  <ul className="govuk-list govuk-list--bullet">
                    {item.errors.map((detail, index) => (
                      <li key={`${index}-${detail}`}>{detail}</li>
                    ))}
                  </ul>
                </details>
              )}
              {item.section.page.pageType !== 'list' ? (
                <dl className="govuk-summary-list">
                  {item.headings.map((heading, index) => (
                    <div className="govuk-summary-list__row" key={`${index}-${heading}`}>
                      <dt className="govuk-summary-list__key">{heading}</dt>
                      <dd
                        className="govuk-summary-list__value"
                        style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                      >
                        {String(item.rows[0]?.[index] ?? '') || '-'}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <Preview
                  tabIndex={0}
                  role="region"
                  aria-label={`${item.section.title} import data`}
                >
                  <table className="govuk-table">
                    <thead>
                      <tr>
                        {item.headings.map((heading, index) => (
                          <th
                            className="govuk-table__header"
                            scope="col"
                            key={`${index}-${heading}`}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {item.rows.map((row, index) => (
                        <tr key={index}>
                          {row.map((value, column) => (
                            <td className="govuk-table__cell" key={column}>
                              {String(value ?? '') || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Preview>
              )}
            </section>
          ))}
          {error && (
            <p className="govuk-error-message govuk-!-margin-top-4" role="alert">
              {error}
              {startedSaving
                ? ` ${saved.length} sections saved. Retry continues the confirmed import.`
                : ''}
            </p>
          )}
          {busy && (
            <p className="govuk-body" role="status">
              {startedSaving
                ? `Importing sections… ${saved.length} saved.`
                : 'Reading workbook and preparing data…'}
            </p>
          )}
          <div className="govuk-button-group govuk-!-margin-top-5">
            <button
              type="button"
              className="govuk-button"
              disabled={
                busy ||
                (!plan && activeConflicts.some((conflict) => !selections[conflict.key])) ||
                (!plan &&
                  !sections.some((section) => choices[section.id]?.selected && hasCdrData(section)))
              }
              onClick={() => void (plan ? confirm() : preview())}
            >
              {plan
                ? startedSaving
                  ? 'Retry import'
                  : 'Confirm import'
                : 'Import selected sections'}
            </button>
            {plan && !startedSaving && (
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                disabled={busy}
                onClick={() => {
                  setPlan(undefined);
                  setError('');
                }}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              disabled={busy}
              onClick={close}
            >
              Cancel
            </button>
          </div>
        </BulkTableModal>
      )}
    </>
  );
}
